// Library Methods
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useCallback, useMemo } from "react";

// Styles
import {
  PageContainerWrapper,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  ThemeProvider as LightThemeProvider,
  useTheme,
} from "@mui/material/styles";
import Loader from "src/components/shared/loaders/Loader/Loader";
import SnackbarMessage from "src/components/shared/alerts/SnackbarMessage/SnackbarMessage";
import Breadcrumb from "src/components/containers/shared/Breadcrumb/Breadcrumb";
import ReportButtons from "src/components/containers/shared/ReportButtons/ReportButtons";
import PrintButton from "src/components/containers/shared/ReportButtons/PrintButton/PrintButton";
import ReportError from "src/components/containers/shared/ReportError/ReportError";
import ProgressionReportSection from "src/components/containers/shared/ProgressionReportSection/ProgressionReportSection";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";
import { setupTheme } from "src/utils/theme/themeHelper";

// Hooks
import { usePatientById } from "src/services/patients/hooks/usePatientById";
import { useProgressionAnalysisById } from "src/services/exams/hooks/progression-analyses/useProgressionAnalysisById";
import { useProgressionReportInfo } from "src/services/exams/hooks/progression-reports/useProgressionReportInfo";
import { useProgressionAccessInfo } from "src/services/exams/hooks/progression-reports/useProgressionAccessInfo";
import { useGenerateProgressionFile } from "src/services/exams/hooks/progression-reports/useGenerateProgressionFile";
import useReportGeneration from "src/hooks/reports/useReportGeneration";
import useProgressionAnalysisValidation from "src/hooks/progression-analyses/useProgressionAnalysisValidation";

// Types
import {
  PROGRESSION_ANALYSES_TAB_ID,
  ReportFileType,
} from "src/constants/enums";
import { ExamSectionReportType } from "src/services/exams/types";

const ProgressionAnalysisReport = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { clinicInfo } = useClinicContext();
  const { snackbarInfo, setSnackbarInfo } = useSnackbarContext();

  // The report is always printed/exported on a fixed white page, regardless
  // of the app's light/dark setting — locking descendants to the light theme
  // here (rather than patching every Report* component's colors individually)
  // keeps that page legible no matter what mode the rest of the app is in.
  const languageKey = i18n.language as keyof typeof priorityLanguageMap;
  const lightTheme = useMemo(
    () => setupTheme(theme.direction, languageKey, "light"),
    [theme.direction, languageKey]
  );

  const location = useLocation();
  const patientId = location?.pathname?.split("/")?.[2];
  const analysisId = location?.pathname?.split("/")?.[4];

  const {
    data: patientData,
    isLoading: isLoadingPatient,
    isFetching: isFetchingPatient,
    isRefetching: isRefetchingPatient,
  } = usePatientById(clinicInfo?.id, {
    patientId,
  });

  const {
    data: analysisData,
    isLoading: isLoadingAnalysis,
    isFetching: isFetchingAnalysis,
    isRefetching: isRefetchingAnalysis,
  } = useProgressionAnalysisById(clinicInfo?.id, {
    patientId,
    analysisId,
  });

  const {
    shouldDisplayReport,
    enrichedExams,
    enrichedExternalExams,
    isValidating,
    commentData,
    getComments,
  } = useProgressionAnalysisValidation({
    analysisData,
    analysisId,
    patientId,
  });

  const isLoading = useMemo(
    () =>
      isLoadingPatient ||
      isFetchingPatient ||
      isRefetchingPatient ||
      isLoadingAnalysis ||
      isFetchingAnalysis ||
      isRefetchingAnalysis ||
      isValidating,
    [
      isLoadingPatient,
      isFetchingPatient,
      isRefetchingPatient,
      isLoadingAnalysis,
      isFetchingAnalysis,
      isRefetchingAnalysis,
      isValidating,
    ]
  );

  const formattedPatientName = useMemo(
    () =>
      getTruncatedText(`${patientData?.firstName} ${patientData?.lastName}`),
    [patientData]
  );

  const { mutateAsync: getReportInfo } = useProgressionReportInfo(
    clinicInfo?.id
  );
  const { mutateAsync: getAccessInfo } = useProgressionAccessInfo(
    clinicInfo?.id
  );
  const { mutateAsync: generateReportFile } = useGenerateProgressionFile(
    clinicInfo?.id
  );

  const getReportInfoWrapper = useCallback(
    async () => await getReportInfo({ patientId, analysisId }),
    [patientId, analysisId, getReportInfo]
  );

  const getAccessInfoWrapper = useCallback(
    async (fileType: ReportFileType) =>
      await getAccessInfo({ patientId, analysisId, fileType }),
    [patientId, analysisId, getAccessInfo]
  );

  const generateReportFileWrapper = useCallback(
    async (fileType: ReportFileType) =>
      await generateReportFile({ patientId, analysisId, fileType }),
    [patientId, analysisId, generateReportFile]
  );

  const reportGenerationProps = useReportGeneration({
    getReportInfo: getReportInfoWrapper,
    getAccessInfo: getAccessInfoWrapper,
    generateReportFile: generateReportFileWrapper,
  });

  // if the analysis exams don't exist, don't show the download buttons
  const showDownloadButtons = useMemo(
    () =>
      Boolean(
        shouldDisplayReport &&
          (enrichedExams?.length > 0 || enrichedExternalExams?.length > 0)
      ),
    [enrichedExams, enrichedExternalExams, shouldDisplayReport]
  );

  return (
    <ReportContext.Provider
      value={{
        patient: patientData,
        exam: undefined,
        progressionAnalysis: analysisData,
        externalExam: undefined,
        reportData: {},
        refetchProgressionAnalysisComment: getComments,
        refetchExamComment: async () => {},
        getReportInfoData: reportGenerationProps.getReportInfoData,
      }}
    >
      <ReportSectionContext.Provider
        value={{
          sectionId: analysisData?.id ?? "",
          eyeType: analysisData?.eye ?? "",
          commentData,
          duration: "",
          durationStartTime: "",
          examReport: {} as ExamSectionReportType,
        }}
      >
        <LightThemeProvider theme={lightTheme}>
          <Box
            sx={{
              ...PageContainerWrapper(theme),
              backgroundColor: "#FFF",
              // MuiTypography forces `color: inherit`, so plain text only
              // picks up a theme's palette via CSS inheritance — the nested
              // LightThemeProvider above doesn't touch that chain by itself,
              // so it must be seeded here explicitly for it to cascade.
              color: getSurfaceTokens().onSurface,
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <Typography fontSize="16px" fontWeight={600}>
              {t("Progression Analysis Report")}
            </Typography>

            <Breadcrumb
              isLoading={isLoading}
              breadcrumbLink={{
                href: "/patients",
                title: `${t("Back to Exam List")} (${formattedPatientName})`,
              }}
              breadcrumbParts={[]}
              breadcrumbState={{
                stateId: patientData?.id,
                fromReport: true,
                selectedTab: PROGRESSION_ANALYSES_TAB_ID,
              }}
              optionalButton={
                <PrintButton
                  isLoading={isLoading}
                  showDownloadButtons={showDownloadButtons}
                  fileInfo={reportGenerationProps.fileInfo.PDF}
                  reportInfoError={reportGenerationProps.reportInfoError}
                  printFile={reportGenerationProps.printFile}
                />
              }
            />

            <ReportButtons
              patient={patientData}
              examId={analysisId}
              isLoading={isLoading}
              showTransferButton={false}
              showDownloadButtons={showDownloadButtons}
              reportGenerationProps={reportGenerationProps}
            />

            {isLoading && (
              <Box sx={{ padding: "24px" }}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  py={10}
                  px="24px"
                  gap="24px"
                  textAlign="center"
                >
                  <Typography fontSize="24px" fontWeight={600}>
                    {t("Loading Your Report...")}
                  </Typography>
                  <Loader containerProps={{ height: "100px" }} />
                </Box>
              </Box>
            )}
            {!isLoading && !shouldDisplayReport && (
              <ReportError title={t("Unable to Retrieve Your Report.")} />
            )}
            {!isLoading && shouldDisplayReport && (
              <ProgressionReportSection
                patientId={patientId}
                analysisId={analysisId}
                enrichedExams={enrichedExams}
                enrichedExternalExams={enrichedExternalExams}
              />
            )}

            {/* Snackbar success and error messages */}
            <SnackbarMessage
              snackbarInfo={snackbarInfo}
              setSnackbarInfo={setSnackbarInfo}
            />
          </Box>
        </LightThemeProvider>
      </ReportSectionContext.Provider>
    </ReportContext.Provider>
  );
};

export default ProgressionAnalysisReport;
